import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FiloApiClient } from '@/api/FiloApiClient';
import type {
  ChatMessage,
  ChatResponse,
  RegisteredRobot,
  SpeechRequest,
  TranscribeCredentialsResponse,
} from '@/api/types';
import { useAuth } from '@/auth/AuthProvider';
import { playSpeechAudio } from '@/speech/speechAudioContext';
import type { AppEdge, AppNode } from '@/flow/types';
import { BatchRobotService } from './BatchRobotService';
import { MultiRobotConnectionManager, type FlowRunTargets, type MultiRobotSnapshot } from './MultiRobotConnectionManager';
import { RobotDirectoryClient, type DirectoryEvent, type RobotDirectoryConnectionState } from './RobotDirectoryClient';
import { applyRobotDirectoryEvent, RobotDirectoryRevision, RobotStatusEventOrder } from './robotDirectoryState';
import { nextDirectoryLastError } from './directoryStatus';
import type { RpcCallOptions, RpcService } from './rpcTypes';
import {
  UserFlowConnectionManager,
  type UserFlowManagerSnapshot,
} from './UserFlowConnectionManager';
import { UserFlowIceServerProvider } from './UserFlowIceServerProvider';
import { SessionReplacedNotice } from './SessionReplacedNotice';
import { OFFLINE_MODE } from '@/config/offline';
import { LocalRemoteProvider } from './LocalRemoteProvider';

type Runtime = {
  api: FiloApiClient;
  directory: RobotDirectoryClient;
  manager: MultiRobotConnectionManager;
  userFlow: UserFlowConnectionManager;
  unsubscribeDirectory: () => void;
  unsubscribeDirectoryState: () => void;
};

export type FiloRemoteContextValue = {
  robots: RegisteredRobot[];
  snapshot: MultiRobotSnapshot;
  directoryState: RobotDirectoryConnectionState;
  directoryLastError: string | null;
  connectedCount: number;
  loadingRobots: boolean;
  refreshRobots: () => Promise<void>;
  connectRobots: (robotIds: string[]) => Promise<void>;
  disconnectRobot: (robotId: string) => Promise<void>;
  reconnectRobot: (robotId: string) => Promise<void>;
  changeVolume: (level: number) => Promise<number>;
  setRepresentative: (robotId: string | null) => void;
  createRunSnapshot: () => FlowRunTargets;
  createBatchService: (targets: FlowRunTargets) => BatchRobotService;
  setRunInProgress: (running: boolean, targets?: FlowRunTargets) => void;
  subscribeRunTargets: (targets: FlowRunTargets, listener: (activeRobotIds: string[]) => void) => () => void;
  hasActiveTargets: (targets: FlowRunTargets) => boolean;
  representativeStream: MediaStream | null;
  acquireRepresentativeCamera: () => Promise<string | null>;
  acquireCamera: (robotId: string) => Promise<string | null>;
  migrateRepresentativeCamera: (token: string) => Promise<boolean>;
  releaseCamera: (token: string) => Promise<void>;
  /** Camera-preview microphone. Defaults to the micOn/micOff RPC when a transport does not override it. */
  setRobotMicrophone?: (robotId: string, enabled: boolean) => Promise<void>;
  callRobot: <T = unknown>(
    robotId: string,
    service: RpcService,
    data?: unknown,
    options?: RpcCallOptions,
  ) => Promise<T>;
  subscribeRobotButton: (robotId: string, listener: (event: import('./RobotConnectionSession').RobotButtonEvent) => void) => () => void;
  createChatResponse: (messages: ChatMessage[]) => Promise<ChatResponse>;
  createTranscribeCredentials: () => Promise<TranscribeCredentialsResponse>;
  playSimulationSpeech: (data: SpeechRequest, signal?: AbortSignal) => Promise<void>;
  synthesizeSpeech: (data: SpeechRequest, signal?: AbortSignal) => Promise<ArrayBuffer>;
  userFlow: UserFlowManagerSnapshot;
  refreshUsers: () => boolean;
  selectUserFlow: (connectionId: string | null) => Promise<void>;
  publishFlow: (nodes: AppNode[], edges: AppEdge[]) => void;
  disposeAll: () => Promise<void>;
};

const EMPTY_SNAPSHOT: MultiRobotSnapshot = {
  sessions: {},
  preparationFailures: {},
  representativeRobotId: null,
  isConnectionChangeInProgress: false,
  isRunInProgress: false,
  runTargets: null,
  lastError: null,
};
// eslint-disable-next-line react-refresh/only-export-components
export const FiloRemoteContext = createContext<FiloRemoteContextValue | null>(null);
const EMPTY_USER_FLOW: UserFlowManagerSnapshot = {
  users: [],
  selectedConnectionId: null,
  selectedUser: null,
  connectionState: 'idle',
  remoteFlow: null,
  lastError: null,
};

export function FiloRemoteProvider({ children }: { children: ReactNode }) {
  return OFFLINE_MODE ? <LocalRemoteProvider>{children}</LocalRemoteProvider> : <OnlineRemoteProvider>{children}</OnlineRemoteProvider>;
}

function OnlineRemoteProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const robotDirectoryRevisionRef = useRef(new RobotDirectoryRevision());
  const robotStatusOrderRef = useRef(new RobotStatusEventOrder());
  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [snapshot, setSnapshot] = useState<MultiRobotSnapshot>(EMPTY_SNAPSHOT);
  const [directoryState, setDirectoryState] = useState<RobotDirectoryConnectionState>('idle');
  const [directoryLastError, setDirectoryLastError] = useState<string | null>(null);
  const [robots, setRobots] = useState<RegisteredRobot[]>([]);
  const robotsRef = useRef<RegisteredRobot[]>([]);
  const [loadingRobots, setLoadingRobots] = useState(true);
  const [userFlow, setUserFlow] = useState<UserFlowManagerSnapshot>(EMPTY_USER_FLOW);
  const [sessionReplaced, setSessionReplaced] = useState(false);
  const clientInstanceIdRef = useRef(crypto.randomUUID());

  const closeReplacedSessionNotice = useCallback(() => {
    authRef.current.endSession();
  }, []);

  const replaceRobots = useCallback((next: RegisteredRobot[]) => {
    robotsRef.current = next;
    setRobots(next);
  }, []);

  const applyDirectoryEvent = useCallback((event: DirectoryEvent, manager: MultiRobotConnectionManager) => {
    if (event.type === 'sessionReplaced') {
      void manager.disposeAll();
      setSessionReplaced(true);
      return;
    }
    if (event.type === 'error') {
      setDirectoryLastError((current) => nextDirectoryLastError(current, { event }));
      manager.handleDirectoryEvent(event);
      return;
    }
    if (event.type === 'directorySnapshot') {
      try {
        const parsed = applyRobotDirectoryEvent([], event);
        const next = robotStatusOrderRef.current.mergeSnapshot(parsed, event.occurredAt);
        robotDirectoryRevisionRef.current.recordDirectoryUpdate();
        setDirectoryLastError((current) => nextDirectoryLastError(current, { event }));
        replaceRobots(next);
      } catch (error) {
        setDirectoryLastError((current) => nextDirectoryLastError(current, {
          event: {
            type: 'error',
            message: error instanceof Error ? error.message : 'Directory snapshot の形式が不正です',
          },
        }));
        // Keep the last validated directory when a malformed snapshot arrives.
      }
      return;
    }
    if (event.type === 'robotStatus' && typeof event.robotId === 'string' && typeof event.online === 'boolean') {
      if (!robotStatusOrderRef.current.acceptStatus(event)) return;
      manager.handleDirectoryEvent(event);
      robotDirectoryRevisionRef.current.recordDirectoryUpdate();
      replaceRobots(applyRobotDirectoryEvent(robotsRef.current, event));
      return;
    }
    manager.handleDirectoryEvent(event);
  }, [replaceRobots]);

  useEffect(() => {
    let active = true;
    const api = new FiloApiClient(() => authRef.current.getAccessToken());
    const directory = new RobotDirectoryClient(
      () => api.createWebSocketTicket(),
      (event) => api.reportTelemetry(event),
      {
        clientInstanceId: clientInstanceIdRef.current,
        displayName: authRef.current.user?.username ?? 'Unknown user',
        flowSharing: true,
      },
    );
    const manager = new MultiRobotConnectionManager(api, directory);
    const userFlowIce = new UserFlowIceServerProvider(api);
    const userFlowManager = new UserFlowConnectionManager(directory, () => userFlowIce.get());
    const unsubscribeDirectory = directory.subscribe((event) => applyDirectoryEvent(event, manager));
    const unsubscribeDirectoryState = directory.subscribeState((state) => {
      if (active) {
        setDirectoryState(state);
        setDirectoryLastError((current) => nextDirectoryLastError(current, { state }));
      }
    });
    const nextRuntime = {
      api,
      directory,
      manager,
      userFlow: userFlowManager,
      unsubscribeDirectory,
      unsubscribeDirectoryState,
    };
    setRuntime(nextRuntime);
    setSnapshot(manager.getSnapshot());
    setLoadingRobots(true);
    void directory.connect();
    const requestRevision = robotDirectoryRevisionRef.current.capture();
    void api.getRobots()
      .then((items) => {
        if (active) robotDirectoryRevisionRef.current.applyIfCurrent(requestRevision, () => replaceRobots(items));
      })
      .catch(() => {
        if (active) robotDirectoryRevisionRef.current.applyIfCurrent(requestRevision, () => replaceRobots([]));
      })
      .finally(() => { if (active) setLoadingRobots(false); });
    const onUnload = () => { void manager.disposeAll(); directory.dispose(); };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      active = false;
      window.removeEventListener('beforeunload', onUnload);
      unsubscribeDirectory();
      unsubscribeDirectoryState();
      userFlowManager.dispose();
      directory.dispose();
      void manager.disposeAll();
    };
  }, [applyDirectoryEvent, replaceRobots]);

  useEffect(() => {
    if (!runtime || !auth.user) return;
    runtime.directory.updateOperatorPresence({
      clientInstanceId: clientInstanceIdRef.current,
      displayName: auth.user.username,
      flowSharing: true,
    });
  }, [auth.user, runtime]);

  useEffect(() => {
    if (!runtime) return;
    setSnapshot(runtime.manager.getSnapshot());
    return runtime.manager.subscribe(() => setSnapshot(runtime.manager.getSnapshot()));
  }, [runtime]);

  useEffect(() => {
    if (!runtime) return;
    setUserFlow(runtime.userFlow.getSnapshot());
    return runtime.userFlow.subscribe(() => setUserFlow(runtime.userFlow.getSnapshot()));
  }, [runtime]);

  const requireRuntime = useCallback(() => {
    if (!runtime) throw new Error('接続機能を準備しています');
    return runtime;
  }, [runtime]);

  const refreshRobots = useCallback(async () => {
    setLoadingRobots(true);
    const requestRevision = robotDirectoryRevisionRef.current.capture();
    try {
      const items = await requireRuntime().api.getRobots();
      robotDirectoryRevisionRef.current.applyIfCurrent(requestRevision, () => replaceRobots(items));
    } finally {
      setLoadingRobots(false);
    }
  }, [replaceRobots, requireRuntime]);

  const value = useMemo<FiloRemoteContextValue>(() => ({
    robots,
    snapshot,
    directoryState,
    directoryLastError,
    connectedCount: Object.values(snapshot.sessions).filter((session) => session.state === 'connected').length,
    loadingRobots,
    refreshRobots,
    connectRobots: async (robotIds) => { await requireRuntime().manager.connectRobots(robotIds); },
    disconnectRobot: (robotId) => requireRuntime().manager.disconnectRobot(robotId),
    reconnectRobot: (robotId) => requireRuntime().manager.reconnectRobot(robotId),
    changeVolume: (level) => requireRuntime().manager.changeVolume(level),
    setRepresentative: (robotId) => requireRuntime().manager.setRepresentative(robotId),
    createRunSnapshot: () => requireRuntime().manager.createRunSnapshot(),
    createBatchService: (targets) => new BatchRobotService(requireRuntime().manager, targets),
    setRunInProgress: (running, targets) => requireRuntime().manager.setRunInProgress(running, targets),
    subscribeRunTargets: (targets, listener) => requireRuntime().manager.subscribeRunTargets(targets, listener),
    hasActiveTargets: (targets) => requireRuntime().manager.getActiveRunRobotIds(targets).length > 0,
    representativeStream: runtime?.manager.getRepresentativeStream() ?? null,
    acquireRepresentativeCamera: () => requireRuntime().manager.acquireRepresentativeCamera(),
    acquireCamera: (robotId) => requireRuntime().manager.acquireCamera(robotId),
    migrateRepresentativeCamera: (token) => requireRuntime().manager.migrateRepresentativeCamera(token),
    releaseCamera: (token) => requireRuntime().manager.releaseCamera(token),
    callRobot: (robotId, service, data, options) => (
      requireRuntime().manager.callRobot(robotId, service, data, options)
    ),
    subscribeRobotButton: (robotId, listener) => requireRuntime().manager.subscribeRobotButton(robotId, listener),
    createChatResponse: (messages) => requireRuntime().api.createChatResponse(messages),
    createTranscribeCredentials: () => requireRuntime().api.createTranscribeCredentials(),
    playSimulationSpeech: async (data, signal) => {
      const audio = await requireRuntime().api.synthesizeSpeech(data, signal);
      await playSpeechAudio(audio, signal);
    },
    synthesizeSpeech: (data, signal) => requireRuntime().api.synthesizeSpeech(data, signal),
    userFlow,
    refreshUsers: () => requireRuntime().directory.refreshUserDirectory(),
    selectUserFlow: (connectionId) => requireRuntime().userFlow.selectUser(connectionId),
    publishFlow: (nodes, edges) => requireRuntime().userFlow.publish(nodes, edges),
    disposeAll: async () => {
      const current = runtime;
      if (!current) return;
      current.userFlow.dispose();
      current.directory.dispose();
      await current.manager.disposeAll();
    },
  }), [directoryLastError, directoryState, loadingRobots, refreshRobots, requireRuntime, robots, runtime, snapshot, userFlow]);

  return (
    <FiloRemoteContext.Provider value={value}>
      {children}
      {sessionReplaced && <SessionReplacedNotice onClose={closeReplacedSessionNotice} />}
    </FiloRemoteContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useFiloRemote = () => {
  const value = useContext(FiloRemoteContext);
  if (!value) throw new Error('FiloRemoteProvider がありません');
  return value;
};
